import { useEffect, useState } from 'react';
import ProgressBar from 'components/raffle/ProgressBar';
import { colors } from 'utils/constants';
import RoundedButton from 'components/RoundedButton';
import CircledText from 'components/CircledText';
import $ from 'jquery';

interface INumberPicker {
  minNumber?: number;
  maxNumber: number;
  prepopulateNumbers: number[];
  selectLimit: number;
  colour: string;
  textColour?: string;
  onSelect: (numbers: number[]) => void;
}
const NumberPicker = ({
  minNumber = 1,
  maxNumber,
  prepopulateNumbers,
  selectLimit,
  colour,
  textColour,
  onSelect,
}: INumberPicker) => {
  const allNumbers = Array.from(
    new Array(maxNumber - minNumber + 1),
    (x, i) => i + minNumber,
  );
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>(
    prepopulateNumbers || [],
  );
  const isNumberSelected = (number: number) => selectedNumbers.includes(number);

  return (
    <div className="numberPicker fullWidth flexRow">
      {allNumbers.map((number, i) => (
        <CircledText
          key={i}
          lineColour={isNumberSelected(number) ? textColour || 'white' : colour}
          circleColour={colour}
          backgroundColour={isNumberSelected(number) ? colour : 'white'}
          text={number.toString()}
          additionalClassName="circledNumber"
          onClick={() => {
            if (isNumberSelected(number)) {
              // Remove this number from selected numbers array
              const newSelectedNumbers = selectedNumbers.filter(
                (n) => n !== number,
              );
              setSelectedNumbers(newSelectedNumbers);
              onSelect(newSelectedNumbers);
            } else if (selectedNumbers.length < selectLimit) {
              // Add this number to selected numbers array
              const newSelectedNumbers = selectedNumbers.concat([number]);
              setSelectedNumbers(newSelectedNumbers);
              onSelect(newSelectedNumbers);
            } else if (selectLimit === 1) {
              // If the picker only requires a total of 1 number, then just
              // switch the number
              const newSelectedNumbers = [number];
              setSelectedNumbers(newSelectedNumbers);
              onSelect(newSelectedNumbers);
            }
          }}
        />
      ))}

      <style jsx>
        {`
          .numberPicker {
            flex-wrap: wrap;
            flex-shrink: 0;
          }

          :global(.numberPicker .circledText) {
            width: ${100 / 7 - (2 * 7) / 7}%;
            padding-top: ${100 / 7 - 3.3333}%;
            margin: 1%;
          }
        `}
      </style>
    </div>
  );
};
interface IWeeklyDrawNumberPicker {
  prepopulateMainNumbers: number[];
  prepopulateBonusNumbers: number[];
  onConfirmNumbers: (main: number[], bonus: number[]) => void;
  numberRanges: number[];
  bonusNumberRanges: number[];
  /** Called when the user interacts with the numbers, so that consumers of the
   * component can mark the page as "dirty"
   */
  onNumberChange?: () => void;
}
const WeeklyDrawNumberPicker = ({
  prepopulateMainNumbers,
  prepopulateBonusNumbers,
  onConfirmNumbers,
  numberRanges,
  bonusNumberRanges,
  onNumberChange,
}: IWeeklyDrawNumberPicker) => {
  const mainNumberCount = numberRanges.length;
  const bonusNumberCount = bonusNumberRanges.length;

  const [selectedMainNumbers, setSelectedMainNumbers] = useState<number[]>(
    prepopulateMainNumbers || [],
  );
  const [selectedBonusNumbers, setSelectedBonusNumbers] = useState<number[]>(
    prepopulateBonusNumbers || [],
  );

  const allNumbersHaveBeenSelected =
    selectedMainNumbers.length === mainNumberCount &&
    selectedBonusNumbers.length === bonusNumberCount;

  useEffect(() => {
    // Only scroll to the bonus ball picker if we started with no numbers
    const startedWithNoNumbers =
      prepopulateMainNumbers.length < mainNumberCount;
    if (
      startedWithNoNumbers &&
      selectedMainNumbers.length === mainNumberCount
    ) {
      $('#popupScrollBox').animate({
        scrollTop: $('#bonusNumberPicker').offset()?.top,
      });
    }
  }, [selectedMainNumbers.length]);

  return (
    <div className="weeklyDrawNumberPicker fullWidth flexCentered">
      <div className="main numberPickerSection fullWidth flexCentered">
        <div className="headingContainer fullWidth flexRow">
          <p className="heading">Choose {mainNumberCount} main numbers</p>

          <p className="progressCounter">
            {selectedMainNumbers.length} of {mainNumberCount}
          </p>
        </div>

        <ProgressBar
          currentStep={selectedMainNumbers.length}
          totalSteps={mainNumberCount}
        />

        <div className="mainNumberPicker fullWidth flexCentered">
          <NumberPicker
            maxNumber={numberRanges[0]}
            selectLimit={mainNumberCount}
            prepopulateNumbers={selectedMainNumbers}
            colour={colors.primaryBrandGray}
            onSelect={(selectedNumbers) => {
              setSelectedMainNumbers(selectedNumbers);
              onNumberChange?.();
            }}
          />
        </div>
      </div>

      {bonusNumberCount > 0 && (
        <div
          id="bonusNumberPicker"
          className="bonus numberPickerSection fullWidth flexCentered noTopSpacing"
        >
          <div className="headingContainer fullWidth flexRow">
            <p className="heading">
              Choose {bonusNumberCount} bonus number
              {bonusNumberCount > 1 ? 's' : ''}
            </p>

            <p className="progressCounter">
              {selectedBonusNumbers.length} of {bonusNumberCount}
            </p>
          </div>

          <ProgressBar
            currentStep={selectedBonusNumbers.length}
            totalSteps={bonusNumberCount}
          />

          <div className="bonusNumberPicker fullWidth flexCentered">
            <NumberPicker
              maxNumber={bonusNumberRanges[0]}
              selectLimit={bonusNumberCount}
              prepopulateNumbers={selectedBonusNumbers}
              colour="#e1e1e1"
              textColour="#4a4a4a"
              onSelect={(selectedNumbers) => {
                setSelectedBonusNumbers(selectedNumbers);
                onNumberChange?.();
              }}
            />
          </div>
        </div>
      )}

      {/*<div*/}
      {/*  onClick={() => {*/}
      {/*    setSelectedMainNumbers([]);*/}
      {/*    setSelectedBonusNumber([]);*/}
      {/*  }}*/}
      {/*  style={{ marginTop: '2em', cursor: 'pointer' }}*/}
      {/*>*/}
      {/*  <p style={{ fontStyle: 'italic', fontWeight: 700 }}>Clear selection</p>*/}
      {/*</div>*/}

      <RoundedButton
        rebrandDm2025
        onClick={() =>
          onConfirmNumbers(selectedMainNumbers, selectedBonusNumbers)
        }
        disabled={!allNumbersHaveBeenSelected}
        fullWidth
      >
        Confirm
      </RoundedButton>

      <style jsx>
        {`
          .weeklyDrawNumberPicker {
            flex-shrink: 0;
            max-width: 400px;
          }

          .weeklyDrawNumberPicker div {
            flex-shrink: 0;
          }

          .numberPickerSection {
            padding: 2em 0;
            flex-shrink: 0;
          }
          .numberPickerSection.noTopSpacing {
            padding-top: 0;
          }

          .headingContainer {
            justify-content: space-between;
            align-items: center;
            padding: 0 1%;
          }

          .headingContainer p {
            margin: 0;
            font-weight: 700;
            font-size: 1.2em;
            line-height: 1em;
          }

          .headingContainer .progressCounter {
            color: ${colors.primaryBrandGray}90;
          }

          :global(.numberPickerSection .progressBar) {
            margin: 1.5em 0;
            width: 99%;
          }
        `}
      </style>
    </div>
  );
};

export default WeeklyDrawNumberPicker;
